import React, { createContext, useContext, useCallback, useMemo } from 'react';

import { Currency, CurrencyContextType } from '@/types/currency';

// The system runs on Naira only — there is nothing left to fetch, switch,
// or convert to. This provider exists purely so formatCurrency (used all
// over the app to render amounts) keeps a single, stable source of truth.
const NGN: Currency = {
  _id: 'ngn',
  currencyName: 'NGN',
  currencySymbol: '₦',
  currencyRateToUSD: 1,
  currencyCountry: 'Nigeria',
};

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined,
);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const formatCurrency = useCallback((amount: number): string => {
    return `₦${amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }, []);

  // Kept as no-ops (rather than removed) so existing call sites — e.g. right
  // after login — don't need to change even though there's nothing left to
  // switch to or apply.
  const noop = useCallback(() => {}, []);
  const identity = useCallback((amount: number) => amount, []);

  const value: CurrencyContextType = useMemo(
    () => ({
      currencies: [NGN],
      selectedCurrency: NGN,
      setSelectedCurrency: noop,
      applyUserCurrency: noop,
      convertAmount: identity,
      formatCurrency,
      isLoading: false,
    }),
    [formatCurrency, noop, identity],
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
